import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowIcon, BaseButton, Typography } from 'fronton-react';
import { button, icon, hidden } from './styles';
import { clickEvent } from '../utils';

const DEFAULT_RENDER_COUNT = 5;

type AccordionListProps = React.PropsWithChildren<{
  renderCount?: number;
  category?: string;
}>;

export const AccordionList = ({
  children,
  category,
  renderCount = DEFAULT_RENDER_COUNT,
}: AccordionListProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const childrenList = React.Children.toArray(children);

  const hiddenListCount = childrenList.length - renderCount;
  const hasHiddenElements = hiddenListCount > 0;

  const visibleList = !isOpen && hasHiddenElements ? childrenList.slice(0, renderCount) : childrenList;
  const hiddenList = childrenList
    .slice(renderCount, childrenList.length)
    .map((el) => React.cloneElement(el as React.ReactElement, { className: hidden }));

  return (
    <>
      {visibleList}
      {!isOpen && hiddenList}
      {hasHiddenElements && (
        <MoreButton
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          category={category}
          hiddenListCount={hiddenListCount}
        />
      )}
    </>
  );
};

type MoreButtonProps = {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;

  hiddenListCount: number;
  category?: string;
};

const MoreButton = ({ isOpen, setIsOpen, category, hiddenListCount }: MoreButtonProps) => {
  const { t } = useTranslation();

  const eventName = isOpen ? 'subdivisionLess' : 'subdivisionMore';
  const buttonText = isOpen ? t('accordion.opened') : `${t('accordion.closed')} ${hiddenListCount}`;

  const clickHandler = React.useCallback(() => {
    setIsOpen(!isOpen);
    clickEvent(`${category} / ${buttonText}`, eventName, true, 0);
  }, [isOpen, buttonText, category, eventName, setIsOpen]);

  return (
    <li>
      <BaseButton className={button} onClick={clickHandler} data-qa="accordion-button">
        <Typography variant="m" size="body_accent">
          {buttonText}
        </Typography>
        {!isOpen && (
          <span className={icon}>
            <ArrowIcon direction="bottom" />
          </span>
        )}
      </BaseButton>
    </li>
  );
};
